import React, { useContext, useState, useEffect } from 'react';
import { DataContext } from '../context/DataContext';
import { Paper, Typography, TextField, Box, Table, TableBody, TableCell, TableContainer, TableRow, Switch, FormControlLabel } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { TradingData } from '../helpers/CSVParser';
import { SettingsContext } from '../context/SettingsContext';
import { LoadingContext } from '../context/LoadingContext';
import { useTranslation } from '../context/TranslationContext';

const TradeList: React.FC = () => {
  const { dataset, getAllTrades, getTotalInitialCapital } = useContext(DataContext);
  const { darkMode, getLocale, showCurrencySymbol, getCurrencySymbol } = useContext(SettingsContext);
  const { setLoading } = useContext(LoadingContext);
  const { t } = useTranslation();
  const decimals = 2;

  const [pageSize, setPageSize] = useState<number>(15);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [showInfoTable, setShowInfoTable] = useState<boolean>(false);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleInfoTableToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShowInfoTable(event.target.checked);
  };

  const getFilteredTrades = (tradingData: TradingData[]) => {
    const searchTermLower = searchTerm.toLowerCase().trim();
    const searchTermNormalized = searchTermLower.replace(',', '.');
    const locale = getLocale();
    const decimalSeparator = (1.1).toLocaleString(locale).substring(1, 2);
    const decimalDivider = decimalSeparator === '.' ? ',' : '.';
  
    return tradingData.filter(trade => {
      const normalizedTrade = {
        ...trade,
        price: trade.price.toFixed(decimals).replace('.', decimalSeparator).replace(',', decimalDivider),
        contracts: trade.contracts.toFixed(decimals).replace('.', decimalSeparator).replace(',', decimalDivider),
        profit: trade.profit.toFixed(decimals).replace('.', decimalSeparator).replace(',', decimalDivider),
        profitPercent: trade.profitPercent.toFixed(decimals).replace('.', decimalSeparator).replace(',', decimalDivider),
        cumulativeProfit: trade.cumulativeProfit.toFixed(decimals).replace('.', decimalSeparator).replace(',', decimalDivider),
        cumulativeProfitPercent: trade.cumulativeProfitPercent.toFixed(decimals).replace('.', decimalSeparator).replace(',', decimalDivider),
      };
  
      return searchTermNormalized !== "" ? normalizedTrade.customId?.toLowerCase().includes(searchTermNormalized) ||
        normalizedTrade.type.toLowerCase().includes(searchTermNormalized) ||
        normalizedTrade.datetime.toLocaleString().toLowerCase().includes(searchTermNormalized) ||
        normalizedTrade.price.toLowerCase().replace('.', decimalSeparator).replace(',', decimalDivider).includes(searchTermNormalized) ||
        normalizedTrade.contracts.toLowerCase().replace('.', decimalSeparator).replace(',', decimalDivider).includes(searchTermNormalized) ||
        normalizedTrade.profit.toLowerCase().replace('.', decimalSeparator).replace(',', decimalDivider).includes(searchTermNormalized) ||
        normalizedTrade.profitPercent.toLowerCase().replace('.', decimalSeparator).replace(',', decimalDivider).includes(searchTermNormalized) ||
        normalizedTrade.cumulativeProfit.toLowerCase().replace('.', decimalSeparator).replace(',', decimalDivider).includes(searchTermNormalized) ||
        normalizedTrade.cumulativeProfitPercent.toLowerCase().replace('.', decimalSeparator).replace(',', decimalDivider).includes(searchTermNormalized)
        :
        tradingData;
    });
  };

  const getCellClassName = (value: number) => {
    return value >= 0 ? (darkMode ? 'profit-positive-dark' : 'profit-positive') : (darkMode ? 'profit-negative-dark' : 'profit-negative');
  };

  const formatValue = (value: number | undefined, decimals: number, suffix?: string) => {
    if (value === undefined || value === 0 ) return '';
    return `${value.toLocaleString(getLocale(), { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}${suffix}`;
  };

  const formatDate = (date: Date | undefined) => {
    if (!date) return '';
    const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: 'numeric' };
    return date.toLocaleDateString(getLocale(), options);
  };

  const includedFiles = dataset.files.filter(file => file.include);
  let allTrades = getAllTrades().map((trade, index) => ({ ...trade, id: index }));
  // filter parsedData by customStartDate and customEndDate if useAllTrades is false
  if (dataset.useDeepBacktest && dataset.customStartDate) {
      allTrades = allTrades.filter(trade => {
          const tradeDate = new Date(trade.datetime);
          return dataset.customStartDate ? tradeDate >= new Date(dataset.customStartDate) : false;
      });
  }
  if (dataset.useDeepBacktest && dataset.customEndDate) {
      allTrades = allTrades.filter(trade => {
          const tradeDate = new Date(trade.datetime);
          return dataset.customEndDate ? tradeDate <= new Date(dataset.customEndDate) : false;
      });
  }

  useEffect(() => {
    setLoading(true);
    setTimeout(() => { // Simulate a delay for the loading spinner
      setLoading(false);
    }, 50);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataset]);

  const columns: GridColDef[] = [
    { field: 'customId', headerName: t("Source"), flex: 1 },
    { field: 'type', headerName: t("Type"), flex: 1 },
    { field: 'datetime', headerName: t("Date"), flex: 1, 
      valueFormatter: (value) => formatDate(new Date(value)) },
    { field: 'price', headerName: t("Price"), flex: 1, align: 'right', headerAlign: 'right', 
      valueFormatter: (value) => `${formatValue(value, decimals, showCurrencySymbol ? ` ${getCurrencySymbol()}` : '')}` },
    { field: 'contracts', headerName: t("Quantity"), flex: 1, align: 'right', headerAlign: 'right', 
      valueFormatter: (value) => formatValue(value, decimals, '') },
    { field: 'profit', headerName: t("Profit"), flex: 1, align: 'right', headerAlign: 'right', 
      cellClassName: (params) => getCellClassName(params.value as number), 
      valueFormatter: (value) => `${formatValue(value, decimals, showCurrencySymbol ? ` ${getCurrencySymbol()}` : '')}` },
    { field: 'profitPercent', headerName: t("Profit %"), flex: 1, align: 'right', headerAlign: 'right', 
      cellClassName: (params) => getCellClassName(params.value as number), 
      valueFormatter: (value) => `${formatValue(value, decimals, ' %')}` },
    { field: 'cumulativeProfit', headerName: t("Cumulative Profit"), flex: 1, align: 'right', headerAlign: 'right', 
      cellClassName: (params) => getCellClassName(params.value as number), 
      valueFormatter: (value) => `${formatValue(value, decimals, showCurrencySymbol ? ` ${getCurrencySymbol()}` : '')}` },
    { field: 'cumulativeProfitPercent', headerName: t("Cumulative Profit %"), flex: 1, align: 'right', headerAlign: 'right', 
      cellClassName: (params) => getCellClassName(params.value as number), 
      valueFormatter: (value) => `${formatValue(value, decimals, ' %')}` },
    { field: 'total', headerName: t("Total"), flex: 1, align: 'right', headerAlign: 'right', 
      cellClassName: (params) => getCellClassName(params.value as number), 
      valueFormatter: (value) => `${formatValue(value, decimals, showCurrencySymbol ? ` ${getCurrencySymbol()}` : '')}` },
  ];

  return (
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h4" gutterBottom>
            {t("TradeList")}
          </Typography>
          <Box display="flex" alignItems="center">
            <Typography variant="body1" style={{ marginRight: '8px', fontWeight: 'bold', color: darkMode ? '#fff' : '#000' }}>
              {t("Search")}:
            </Typography>
            <TextField
              variant="outlined"
              margin="normal"
              value={searchTerm}
              onChange={handleSearchChange}
              size='small'
              style={{ width: '300px', height: '48px' }}
            />
          </Box>
        </Box>
        <FormControlLabel
          control={<Switch checked={showInfoTable} onChange={handleInfoTableToggle} />}
          label={t("Show Info Table")}
        />
        {showInfoTable && (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell style={{ padding: '4px' }}><strong>{t("Total initial capital")}:</strong></TableCell>
                  <TableCell style={{ padding: '4px' }}>{formatValue(getTotalInitialCapital(), decimals, showCurrencySymbol ? ` ${getCurrencySymbol()}` : '')}</TableCell>
                </TableRow>
                {includedFiles.map(file => (
                  <TableRow key={file.customId}>
                    <TableCell style={{ padding: '4px' }}><strong>{t("Initial capital")} {file.customId}:</strong></TableCell>
                    <TableCell style={{ padding: '4px' }}>{formatValue(file.initialCapital, decimals, showCurrencySymbol ? ` ${getCurrencySymbol()}` : '')}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <Paper elevation={3} style={{ margin: '8px' }}>
          <DataGrid
            rows={getFilteredTrades(allTrades)}
            columns={columns}
            initialState={{
              pagination: {
                paginationModel: { pageSize: pageSize },
              },
            }}
            onPaginationModelChange={(model) => setPageSize(model.pageSize)}
            pageSizeOptions={[10, 15, 25, 50, 100]}
            pagination
            rowHeight={32}
          />
        </Paper>
      </Box>
  );
}

// Add CSS classes for cell styling
const styles = `
  .profit-positive {
    color: green;
  }
  .profit-negative {
    color: red;
  }
  .profit-positive-dark {
    color: #089981;
  }
  .profit-negative-dark {
    color: #FF6347;
  }
`;

// Inject styles into the document head
const styleSheet = document.createElement("style");
styleSheet.type = "text/css";
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);

export default TradeList;
